
import { memo, useCallback, useEffect } from "react";
import { CiCircleInfo } from "react-icons/ci";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import ReactModal from "react-modal";
import { color_black, color_black_ultra_light, color_grey_dark, color_red_default, color_white } from "../../constants/colors";
import { MODAL_Z_INDEX_BASE } from "../../constants/layers";
import { useWindowDimensions } from "../../utils";
import { OnestMedium, OnestNormalSmall } from "../styled/TextComponents";
import {
    bottomSheetDragHandleVisualStyle,
    useBottomSheetDrag,
} from "../../hooks/useBottomSheetDrag";
import { useI18n } from "../../i18n/I18nProvider";

const ModalQuestion = memo(({
    type,
    h2,
    p,
    isOpen,
    onClose,
    yesText,
    noText,
    isButtonRevers = false,
    zIndex = MODAL_Z_INDEX_BASE,
    isLoading = false,
}) => {

    const { t } = useI18n();
    const { width: screenWidth } = useWindowDimensions();
    const isMobile = screenWidth <= 540;

    const handleClose = useCallback(() => {
        if (isLoading) {
            return;
        }
        onClose(false);
    }, [isLoading, onClose]);

    const handleYes = useCallback(() => {
        if (isLoading) {
            return;
        }
        onClose(true);
    }, [isLoading, onClose]);

    const { dragHandleProps, sheetMotionStyle, resetDrag } = useBottomSheetDrag({
        enabled: isMobile,
        onClose: handleClose,
    });

    useEffect(() => {
        if (!isOpen) {
            resetDrag();
        }
    }, [isOpen, resetDrag]);

    const modalContentStyle = isMobile
        ? {
            backgroundColor: color_white,
            borderRadius: "20px 20px 0 0",
            width: "100%",
            height: "auto",
            margin: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 14,
            padding: "14px 16px calc(16px + env(safe-area-inset-bottom))",
            top: "auto",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: zIndex + 1,
            boxSizing: "border-box",
            ...sheetMotionStyle,
        }
        : {
            backgroundColor: color_white,
            borderRadius: "16px",
            width: screenWidth > 560 ? "410px" : screenWidth - 150,
            height: "320px",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            padding: "0px 25px",
            inset: screenWidth > 460 ? "40px" : `calc(50vw - ${screenWidth - 150 / 2}px)`,
            zIndex: zIndex + 1,
        };

    const buttonStyle = (isPrimary) => ({
        backgroundColor: isPrimary
            ? (isButtonRevers ? color_white : color_red_default)
            : (isButtonRevers ? color_red_default : color_white),
        color: isPrimary
            ? (isButtonRevers ? color_black_ultra_light : color_white)
            : (isButtonRevers ? color_white : color_red_default),
        borderRadius: "16px",
        border: isPrimary
            ? (isButtonRevers ? `1px solid ${color_grey_dark}` : "none")
            : (isButtonRevers ? "none" : `1px solid ${color_red_default}`),
        padding: "10px 20px",
        fontSize: "16px",
        fontWeight: "normal",
        width: isMobile ? "100%" : 224,
        height: isMobile ? 48 : 40,
        fontFamily: "Onest",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: "1",
        opacity: isLoading ? 0.7 : 1,
        cursor: isLoading ? "not-allowed" : "pointer",
    });

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={() => {
                if (!isLoading) {
                    onClose(false);
                }
            }}
            style={{
                content: modalContentStyle,
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(8px)",
                    overflow: "hidden",
                    zIndex,
                },
            }}
        >
            {isMobile && (
                <div {...dragHandleProps}>
                    <div style={bottomSheetDragHandleVisualStyle} />
                </div>
            )}
            {type === "success" && <IoMdCheckmarkCircleOutline size={25} />}
            {type === "error" && <CiCircleInfo size={28} />}
            {h2 && (
                <OnestMedium style={{
                    fontSize: isMobile ? "20px" : "24px",
                    lineHeight: "120%",
                    letterSpacing: "-0.02em",
                    textAlign: "center",
                    color: color_black,
                    margin: 0,
                }}>
                    {t(h2)}
                </OnestMedium>
            )}
            <div style={{
                display: "flex",
                flexDirection: "column",
                gap: p ? (isMobile ? 20 : "30px") : "0px",
                alignItems: "center",
                ...(isMobile ? { width: "100%" } : {}),
            }}>
                {p && (
                    <OnestNormalSmall style={{
                        fontSize: isMobile ? "15px" : "16px",
                        textAlign: "center",
                        color: color_black,
                        maxWidth: isMobile ? "100%" : "350px",
                        ...(isMobile ? { lineHeight: "140%" } : {}),
                    }}>
                        {t(p)}
                    </OnestNormalSmall>
                )}
                <div style={{
                    display: "flex",
                    flexDirection: isButtonRevers ? "column-reverse" : "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: isMobile ? 10 : 15,
                    ...(isMobile ? { width: "100%" } : {}),
                }}>
                    <button
                        style={buttonStyle(true)}
                        disabled={isLoading}
                        onClick={handleYes}
                    >
                        {yesText ? t(yesText) : t("ДА")}
                    </button>
                    <button
                        style={buttonStyle(false)}
                        disabled={isLoading}
                        onClick={handleClose}
                    >
                        {noText ? t(noText) : t("НЕТ")}
                    </button>
                </div>
            </div>
        </ReactModal>
    );
});

export default ModalQuestion;
